import emailjs from '@emailjs/browser';

// E-Mail Service Konfiguration
const emailConfig = {
  serviceId: process.env.REACT_APP_EMAIL_SERVICE_ID,
  publicKey: process.env.REACT_APP_EMAIL_PUBLIC_KEY,
  companyEmail: process.env.REACT_APP_COMPANY_EMAIL || 'info@ares-wohnbau.de',
  companyName: process.env.REACT_APP_COMPANY_NAME || 'ARES WOHNBAU',
  enableNotifications: process.env.REACT_APP_ENABLE_EMAIL_NOTIFICATIONS === 'true'
};

// Template IDs für verschiedene Formulare
const templates = {
  contact: process.env.REACT_APP_CONTACT_TEMPLATE_ID,
  propertyInquiry: process.env.REACT_APP_PROPERTY_INQUIRY_TEMPLATE_ID,
  financeInquiry: process.env.REACT_APP_FINANCE_INQUIRY_TEMPLATE_ID,
  careerApplication: process.env.REACT_APP_CAREER_APPLICATION_TEMPLATE_ID,
  propertyValuation: process.env.REACT_APP_PROPERTY_VALUATION_TEMPLATE_ID,
  appointment: process.env.REACT_APP_APPOINTMENT_TEMPLATE_ID,
  acquisitionProfile: process.env.REACT_APP_ACQUISITION_PROFILE_TEMPLATE_ID
};

// EmailJS initialisieren
if (emailConfig.publicKey) {
  emailjs.init(emailConfig.publicKey);
}

/**
 * Basis E-Mail Versand Funktion
 * @param {string} templateId - Template ID für EmailJS
 * @param {Object} templateParams - Parameter für das E-Mail Template
 * @param {string} formType - Art des Formulars für Fehlerbehandlung
 * @returns {Promise} - Erfolg oder Fehler
 */
const sendEmail = async (templateId, templateParams, formType = 'Formular') => {
  if (!emailConfig.enableNotifications) {
    console.log('E-Mail Benachrichtigungen sind deaktiviert');
    return { success: true, message: 'E-Mail Benachrichtigungen sind deaktiviert' };
  }

  if (!emailConfig.serviceId || !templateId || !emailConfig.publicKey) {
    console.error('E-Mail Konfiguration unvollständig:', {
      serviceId: !!emailConfig.serviceId,
      templateId: !!templateId,
      publicKey: !!emailConfig.publicKey
    });
    throw new Error('E-Mail Service ist nicht korrekt konfiguriert');
  }

  try {
    // Timestamp und zusätzliche Metadaten hinzufügen
    const enrichedParams = {
      ...templateParams,
      timestamp: new Date().toLocaleString('de-DE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }),
      company_name: emailConfig.companyName,
      form_type: formType,
      website_url: window.location.origin
    };

    const response = await emailjs.send(
      emailConfig.serviceId,
      templateId,
      enrichedParams
    );

    console.log(`${formType} erfolgreich gesendet:`, response);
    return { 
      success: true, 
      message: `${formType} wurde erfolgreich gesendet`,
      response 
    };

  } catch (error) {
    console.error(`Fehler beim Senden des ${formType}:`, error);
    throw new Error(`E-Mail konnte nicht gesendet werden: ${error.text || error.message}`);
  }
};

/**
 * Allgemeines Kontaktformular
 */
export const sendContactForm = async (formData) => {
  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    phone: formData.phone || 'Nicht angegeben',
    message: formData.message,
    subject: formData.subject || 'Allgemeine Anfrage',
    service: formData.service || 'Nicht spezifiziert',
    to_email: emailConfig.companyEmail
  };

  return sendEmail(templates.contact, templateParams, 'Kontaktformular');
};

/**
 * Immobilien-Anfrage (für Kaufinteresse)
 */
export const sendPropertyInquiry = async (formData, propertyDetails = null) => {
  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    phone: formData.phone || 'Nicht angegeben',
    message: formData.message,
    preferred_contact: formData.preferredContact || 'email',
    property_type: formData.propertyType || 'Nicht spezifiziert',
    budget: formData.budget || 'Nicht angegeben',
    location_preference: formData.locationPreference || 'Nicht angegeben',
    property_id: propertyDetails?.id || 'Allgemeine Anfrage',
    property_title: propertyDetails?.title || 'Nicht spezifiziert',
    property_price: propertyDetails?.price || 'Nicht angegeben',
    to_email: emailConfig.companyEmail
  };

  return sendEmail(templates.propertyInquiry, templateParams, 'Immobilien-Anfrage');
};

/**
 * Finanzierungs-Anfrage
 */
export const sendFinanceInquiry = async (formData) => {
  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    phone: formData.phone || 'Nicht angegeben',
    property_value: formData.propertyValue || 'Nicht angegeben',
    equity: formData.equity || 'Nicht angegeben',
    monthly_income: formData.monthlyIncome || 'Nicht angegeben',
    message: formData.message,
    preferred_contact: formData.preferredContact || 'email',
    to_email: emailConfig.companyEmail
  };

  return sendEmail(templates.financeInquiry, templateParams, 'Finanzierungs-Anfrage');
};

/**
 * Bewerbung (Karriere)
 */
export const sendCareerApplication = async (formData) => {
  const templateParams = {
    from_name: `${formData.vorname} ${formData.nachname}`,
    vorname: formData.vorname,
    nachname: formData.nachname,
    from_email: formData.email,
    telefon: formData.telefon,
    geburtsdatum: formData.geburtsdatum,
    adresse: formData.adresse,
    schulabschluss: formData.schulabschluss,
    ausbildungsberuf: formData.ausbildungsberuf,
    startdatum: formData.startdatum,
    motivation: formData.motivation,
    erfahrungen: formData.erfahrungen || 'Keine Angabe',
    to_email: emailConfig.companyEmail
  };

  return sendEmail(templates.careerApplication, templateParams, 'Bewerbung');
};

/**
 * Immobilien-Bewertung (Verkauf)
 */
export const sendPropertyValuation = async (formData) => {
  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    phone: formData.phone || 'Nicht angegeben',
    property_type: formData.propertyType || 'Nicht spezifiziert',
    living_space: formData.livingSpace || 'Nicht angegeben',
    rooms: formData.rooms || 'Nicht angegeben',
    year_built: formData.yearBuilt || 'Nicht angegeben',
    condition: formData.condition || 'Nicht angegeben',
    location: formData.location || 'Nicht angegeben',
    special_features: formData.specialFeatures || 'Keine',
    preferred_contact: formData.preferredContact || 'email',
    preferred_time: formData.preferredTime || 'Nicht angegeben',
    message: formData.message || 'Keine zusätzlichen Informationen',
    to_email: emailConfig.companyEmail
  };

  return sendEmail(templates.propertyValuation, templateParams, 'Immobilien-Bewertung');
};

/**
 * Termin-Anfrage
 */
export const sendAppointmentRequest = async (formData) => {
  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    telefon: formData.telefon || formData.phone,
    wunschtermin: formData.wunschtermin || formData.appointmentDate,
    service_type: formData.serviceType || 'Beratung',
    nachricht: formData.nachricht || formData.message || 'Keine zusätzlichen Informationen',
    to_email: emailConfig.companyEmail
  };

  return sendEmail(templates.appointment, templateParams, 'Termin-Anfrage');
};

/**
 * Sidebar Kontaktformular (vereinfacht)
 */
export const sendSidebarContact = async (formData) => {
  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    phone: formData.phone || 'Nicht angegeben',
    subject: formData.subject || 'Sidebar Anfrage',
    property_type: formData.propertyType || 'Nicht spezifiziert',
    budget: formData.budget || 'Nicht angegeben',
    message: formData.message || 'Keine Nachricht',
    to_email: emailConfig.companyEmail
  };

  return sendEmail(templates.contact, templateParams, 'Sidebar Kontakt');
};

/**
 * Ankaufsprofil-Formular (Stepper-Design)
 */
export const sendAcquisitionProfile = async (formData) => {
  const templateParams = {
    // Kontaktdaten
    from_name: `${formData.vorname} ${formData.nachname}`,
    vorname: formData.vorname,
    nachname: formData.nachname,
    from_email: formData.email,
    telefon: formData.telefon,
    anrede: formData.anrede || 'Nicht angegeben',
    adresse_abweichend: formData.adresseAbweichend || 'Gleich der Objektadresse',
    
    // Objekt & Standort
    objektart: formData.objektart || 'Nicht angegeben',
    objekt_adresse: formData.adresse || 'Nicht angegeben',
    objekt_plz: formData.plz || 'Nicht angegeben',
    objekt_ort: formData.ort || 'Nicht angegeben',
    grundstuecksgroesse: formData.grundstuecksgroesse ? `${formData.grundstuecksgroesse} m²` : 'Nicht angegeben',
    gebaudeflaeche: formData.gebaudeflaeche ? `${formData.gebaudeflaeche} m²` : 'Nicht angegeben',
    anzahl_wohneinheiten: formData.anzahlWohneinheiten || 'Nicht angegeben',
    baujahr: formData.baujahr || 'Nicht angegeben',
    zustand: formData.zustand || 'Nicht angegeben',
    
    // Eckdaten & Zustand
    anzahl_zimmer: formData.anzahlZimmer || 'Nicht angegeben',
    anzahl_badezimmer: formData.anzahlBadezimmer || 'Nicht angegeben',
    balkon_terrasse: formData.balkonTerrasse ? 'Ja' : 'Nein',
    keller: formData.keller ? 'Ja' : 'Nein',
    garage_stellplatz: formData.garageStellplatz || 'Keine',
    heizungsart: formData.heizungsart || 'Nicht angegeben',
    energieeffizienzklasse: formData.energieeffizienzklasse || 'Nicht angegeben',
    vermietung: formData.vermietung ? 'Ja' : 'Nein',
    mieteinnahmen: formData.mieteinnahmen ? `${formData.mieteinnahmen} €/Monat` : 'Nicht angegeben',
    besonderheiten: formData.besonderheiten || 'Keine',
    
    // Preis & Timing
    preisvorstellung: formData.preisvorstellung ? `${formData.preisvorstellung.toLocaleString('de-DE')} €` : 'Nicht angegeben',
    verhandlungsbasis: formData.verhandlungsbasis ? 'Ja' : 'Nein',
    verkaufszeitraum: formData.verkaufszeitraum || 'Nicht angegeben',
    besichtigungstermine: formData.besichtigungstermine || 'Nicht angegeben',
    notartermin: formData.notartermin || 'Nicht angegeben',
    besondere_wuensche: formData.besondereWuensche || 'Keine',
    
    // Einwilligungen
    dsgvo_einwilligung: formData.dsgvoEinwilligung ? 'Ja' : 'Nein',
    marketing_einwilligung: formData.marketingEinwilligung ? 'Ja' : 'Nein',
    datenverarbeitung: formData.datenverarbeitung ? 'Ja' : 'Nein',
    
    // Referenznummer generieren
    referenznummer: `ARES-${Date.now().toString().slice(-6)}`,
    
    to_email: emailConfig.companyEmail
  };

  return sendEmail(templates.acquisitionProfile, templateParams, 'Ankaufsprofil');
};

/**
 * Hilfsfunktion: E-Mail Konfiguration prüfen
 */
export const checkEmailConfiguration = () => {
  const config = {
    serviceId: !!emailConfig.serviceId,
    publicKey: !!emailConfig.publicKey,
    templatesConfigured: Object.values(templates).filter(Boolean).length,
    enableNotifications: emailConfig.enableNotifications
  };

  console.log('E-Mail Konfiguration:', config);
  return config;
};

/**
 * Standard Erfolgs- und Fehlermeldungen
 */
export const emailMessages = {
  success: {
    contact: 'Vielen Dank für Ihre Anfrage! Wir melden uns schnellstmöglich bei Ihnen.',
    propertyInquiry: 'Ihre Immobilien-Anfrage wurde erfolgreich gesendet. Unser Team wird sich zeitnah bei Ihnen melden.',
    financeInquiry: 'Vielen Dank für Ihre Finanzierungsanfrage! Unser Finanzierungsexperte wird sich in Kürze bei Ihnen melden.',
    careerApplication: 'Vielen Dank für Ihre Bewerbung! Wir prüfen Ihre Unterlagen und melden uns bald bei Ihnen.',
    propertyValuation: 'Ihre Bewertungsanfrage wurde erfolgreich übermittelt. Unser Team wird sich innerhalb von 24 Stunden bei Ihnen melden.',
    appointment: 'Terminanfrage gesendet! Wir kontaktieren Sie zur Bestätigung.',
    sidebar: 'Vielen Dank für Ihre Nachricht! Wir melden uns schnellstmöglich bei Ihnen.',
    acquisitionProfile: 'Ihr Ankaufsprofil wurde erfolgreich erstellt! Wir melden uns innerhalb von 24 Stunden für eine professionelle Bewertung Ihrer Immobilie.'
  },
  error: {
    network: 'Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
    configuration: 'E-Mail Service ist momentan nicht verfügbar. Bitte kontaktieren Sie uns direkt.',
    general: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.'
  }
}; 
